/**
 * Service that fetches Agent Builder configuration from the Credit Control
 * backend.  The response is cached in memory so only one request is made
 * per browser session.
 */

// ============================================================================
// Auth provider injection
// ============================================================================

let _getHeaders: (() => Promise<HeadersInit>) | null = null;
let _apiBaseUrl: string | null = null;

/**
 * Called by AIAssistantProvider on mount to inject auth dependencies
 * from the React component tree into this module-scoped service.
 */
export function setAgentConfigAuthProvider(getHeaders: () => Promise<HeadersInit>, apiBaseUrl: string): void {
  _getHeaders = getHeaders;
  _apiBaseUrl = apiBaseUrl;
}

// ============================================================================
// Types
// ============================================================================

export interface AgentConfig {
  readonly agentBuilderBaseUrl: string;
  readonly chatAssistantAgentId: string;
}

// ============================================================================
// Cache
// ============================================================================

let cachedConfig: AgentConfig | null = null;
let fetchPromise: Promise<AgentConfig> | null = null;

// ============================================================================
// Public API
// ============================================================================

/**
 * Fetch and cache the Agent Builder configuration from the backend.
 *
 * Safe to call multiple times — concurrent callers share the same in-flight
 * promise and subsequent callers receive the cached result.
 */
export async function fetchAgentConfig(): Promise<AgentConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }

  if (fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = (async (): Promise<AgentConfig> => {
    if (!_apiBaseUrl || !_getHeaders) {
      throw new Error('Agent config auth provider not initialised. Call setAgentConfigAuthProvider first.');
    }

    const headers = await _getHeaders();

    const response = await fetch(`${_apiBaseUrl}/api/v1/agent/config`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to fetch agent config: ${response.status} ${response.statusText}`);
    }

    const data = (await response.json()) as AgentConfig;
    cachedConfig = data;
    return data;
  })();

  try {
    return await fetchPromise;
  } catch (error) {
    // Allow retry on failure
    fetchPromise = null;
    throw error;
  }
}

/**
 * Return the cached config synchronously, or `null` if it has not been
 * fetched yet.  Use {@link fetchAgentConfig} to populate the cache.
 */
export function getCachedAgentConfig(): AgentConfig | null {
  return cachedConfig;
}

/**
 * Clear the cached config.
 */
export function clearAgentConfigCache(): void {
  cachedConfig = null;
  fetchPromise = null;
}
